import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../../utils';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { useDetailRows, useSyncTSCC, useTaxRows, useTSCCRows } from '.';
import { useHoaDonMuaDichVuSearchContext } from '../../../context';
import { formSchema, initialFormValues } from '../../../schema';
import { useFormFieldState } from '../../../hooks';
import { useAuth } from '@/contexts/auth-context';
import type { FormDialogProps } from '../types';
import { toast } from '@/hooks/use-toast';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

const recalculateTaxAfterRepeat = createRepeatedTaxRecalculator<MuaHangRecord>({
  getTaxBase: row => Number(row?.tien_nt ?? (Number(row?.gia_nt || 0) * Number(row?.so_luong || 0) || 0)),
  roundValue: Math.round
});

export const useFormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [activeTab, setActiveTab] = useState('info');
  const [taxConfirm, setTaxConfirm] = useState<{ open: boolean; maThueUuid?: string; newTax?: number }>({
    open: false
  });
  const originalTaxValuesRef = useRef<Record<string, number>>({});
  const processedTaxRowUuidsRef = useRef<Set<string>>(
    new Set((initialData?.thue_data || []).map((row: MuaHangRecord) => row?.uuid).filter(Boolean))
  );

  const { entityUnit } = useAuth();
  const formState = useFormFieldState(initialData);
  const { state } = formState;
  const { transactionOptions } = useHoaDonMuaDichVuSearchContext();
  const { data: tinhChatThueList } = useCRUD<MuaHangRecord>({
    endpoint: QUERY_KEYS.TINH_CHAT_THUE,
    initialFetch: true
  });

  const detail = useDetailRows(initialData?.chi_tiet_data || [], state.loai_ck, state.ck_tl_nt, state.ck_yn);
  const tax = useTaxRows(initialData?.thue_data || []);
  const tscc = useTSCCRows(initialData?.tai_san_data || []);
  const constraints = useMemo(
    () => state.taiKhoan?.constraints ?? state.nhaCungCap?.account_data?.constraints ?? [],
    [state.nhaCungCap?.account_data?.constraints, state.taiKhoan?.constraints]
  );
  const schema = useMemo(() => formSchema(constraints), [constraints]);

  useEffect(() => {
    const initialUuids = new Set<string>(
      (initialData?.thue_data || []).map((row: MuaHangRecord) => row?.uuid).filter(Boolean) as string[]
    );
    processedTaxRowUuidsRef.current = initialUuids;
  }, [initialData?.thue_data]);

  useEffect(() => {
    if (tinhChatThueList && tinhChatThueList.length > 0) {
      const firstItem = tinhChatThueList[0];
      tax.rows.forEach((row: MuaHangRecord) => {
        if (row.uuid && !row.ma_tc_thue_data && !processedTaxRowUuidsRef.current.has(row.uuid)) {
          tax.handleCellValueChange(row.uuid, 'ma_tc_thue_data', firstItem);
          processedTaxRowUuidsRef.current.add(row.uuid);
        }
      });
    }
  }, [tax.rows, tinhChatThueList, tax.handleCellValueChange]);

  const repeatDetailRows = useRepeatRowsHandler({
    setRows: detail.setRows,
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  const syncTSCC = useSyncTSCC({
    detailRows: detail.rows,
    enabled: state.ma_gd_code === 'HD6TS' && formMode !== 'view',
    setTSCCRows: tscc.setRows,
    invoiceDate: state.ngay_ct
  });

  useEffect(() => {
    if (formMode !== 'view') {
      syncTSCC();
    }
  }, [syncTSCC, formMode]);

  const totals = useMemo(() => calculateTotals(detail.rows), [detail.rows]);

  const submit = useCallback(
    (data: MuaHangRecord) => {
      if (state.ma_gd_code === 'HD6TS' && (!tscc.rows || tscc.rows.length === 0)) {
        toast({
          description: 'Vui lòng nhập thông tin tài sản/công cụ',
          variant: 'destructive'
        });
        return;
      }

      const submitState = {
        ...state,
        ma_gd: state.ma_gd || transactionOptions[0]?.value || initialData?.ma_gd || 'DV'
      };
      const formData = transformFormData(
        data,
        submitState,
        formMode,
        totals.tong_thue,
        totals.tong_tien,
        totals.tong_chiet_khau,
        totals.tong_thanh_toan,
        detail.rows,
        tax.rows,
        tscc.rows
      );

      onSubmit?.({ ...formData, unit_id: entityUnit?.uuid });
    },
    [
      detail.rows,
      entityUnit?.uuid,
      formMode,
      initialData?.ma_gd,
      onSubmit,
      state,
      tax.rows,
      totals.tong_chiet_khau,
      totals.tong_thanh_toan,
      totals.tong_thue,
      totals.tong_tien,
      transactionOptions,
      tscc.rows
    ]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const importTaxFromDetail = useCallback(() => {
    const grouped: Record<string, MuaHangRecord> = {};
    detail.rows
      .filter(row => row?.ma_thue_data && row?.thue_nt && row?.thue_nt > 0)
      .forEach(row => {
        const taxUuid = row.ma_thue_data?.uuid || JSON.stringify(row.ma_thue_data);
        if (!taxUuid) return;

        if (!grouped[taxUuid]) {
          grouped[taxUuid] = {
            uuid: `tax-${taxUuid}`,
            line: 0,
            ngay_ct0: new Date().toISOString().split('T')[0],
            ma_thue_data: row.ma_thue_data,
            thue_suat: row.ma_thue_data?.thue_suat || 0,
            t_tien_nt: 0,
            t_thue_nt: 0,
            tk_thue_no_data: row.ma_thue_data?.tk_thue_dau_vao_data
          };
        }

        grouped[taxUuid].t_tien_nt += Number(row.tien_nt || 0);
        grouped[taxUuid].t_thue_nt += Number(row.thue_nt || 0);
      });

    const convertedTaxRows = Object.values(grouped).map((item: MuaHangRecord, index: number) => ({
      ...item,
      line: index + 1
    }));

    tax.setRows((prevRows: MuaHangRecord[]) => {
      const newTaxIds = new Set(convertedTaxRows.map(r => r?.ma_thue_data?.uuid || JSON.stringify(r?.ma_thue_data)));
      const kept = (prevRows || []).filter(r => {
        const id = r?.ma_thue_data?.uuid || JSON.stringify(r?.ma_thue_data);
        return !newTaxIds.has(id);
      });

      const nextRows = [...kept, ...convertedTaxRows].map((row, idx) => ({ ...row, line: idx + 1 }));
      const hasChanged =
        nextRows.length !== prevRows.length ||
        nextRows.some((row, index) => {
          const prevRow = prevRows[index];

          return (
            !prevRow ||
            prevRow.uuid !== row.uuid ||
            prevRow.line !== row.line ||
            prevRow.t_tien_nt !== row.t_tien_nt ||
            prevRow.t_thue_nt !== row.t_thue_nt ||
            prevRow.ma_thue_data !== row.ma_thue_data ||
            prevRow.tk_thue_no_data !== row.tk_thue_no_data
          );
        });

      return hasChanged ? nextRows : prevRows;
    });
  }, [detail.rows, tax]);

  const allocateDetailTax = useCallback(
    (maThueUuid: string, totalTax: number) => {
      const targets = detail.rows.filter(r => (r?.ma_thue_data?.uuid || '') === maThueUuid);
      if (targets.length === 0) return;

      const totalTienNt = targets.reduce((sum, r) => sum + Number(r.tien_nt || 0), 0);
      let remainingTax = totalTax;

      detail.setRows(prevRows => {
        let hasChanged = false;

        const nextRows = prevRows.map((row: MuaHangRecord) => {
          const currentTaxUuid = row?.ma_thue_data?.uuid || '';
          if (currentTaxUuid === maThueUuid) {
            const proportion = totalTienNt > 0 ? Number(row.tien_nt || 0) / totalTienNt : 0;
            let allocatedTax = Math.round(totalTax * proportion * 10) / 10;

            if (targets[targets.length - 1].uuid === row.uuid) {
              allocatedTax = remainingTax;
            } else {
              remainingTax -= allocatedTax;
            }

            if (row.thue_nt === allocatedTax) return row;

            hasChanged = true;

            return {
              ...row,
              thue_nt: allocatedTax
            };
          }
          return row;
        });

        return hasChanged ? nextRows : prevRows;
      });
    },
    [detail]
  );

  const allocateAllDetailTax = useCallback(
    (totalTax: number) => {
      const totalTienNt = detail.rows.reduce((sum: number, r: MuaHangRecord) => sum + Number(r.tien_nt || 0), 0);
      if (totalTienNt <= 0 || !Number.isFinite(totalTienNt)) return;

      let remainingTax = Number(totalTax || 0);
      let hasChanged = false;

      const updated = detail.rows.map((row: MuaHangRecord, index: number) => {
        const numerator = Number(row.tien_nt || 0);
        const proportion = numerator / totalTienNt;
        const allocated =
          index === detail.rows.length - 1 ? remainingTax : Math.round(Number(totalTax) * proportion * 10) / 10;
        remainingTax = Math.round((remainingTax - allocated) * 10) / 10;

        if (row.thue_nt === allocated) return row;

        hasChanged = true;
        return { ...row, thue_nt: allocated };
      });

      if (hasChanged) {
        detail.setRows(updated);
      }
    },
    [detail]
  );

  const taxCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      if (field === 't_thue_nt' && !originalTaxValuesRef.current[rowUuid]) {
        const taxRow: MuaHangRecord = tax.rows.find((r: MuaHangRecord) => r?.uuid === rowUuid) || {};
        originalTaxValuesRef.current[rowUuid] = Number(taxRow?.t_thue_nt || 0);
      }

      tax.handleCellValueChange(rowUuid, field, newValue);
    },
    [tax]
  );

  const taxCellCommit = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      if (field === 't_thue_nt') {
        const taxRow: MuaHangRecord = tax.rows.find((r: MuaHangRecord) => r?.uuid === rowUuid) || {};
        const originalValue = originalTaxValuesRef.current[rowUuid] || 0;
        const newTaxValue = Number(newValue || 0);

        if (originalValue !== newTaxValue) {
          const maThueUuid = taxRow?.ma_thue_data?.uuid || '';
          setTaxConfirm({ open: true, maThueUuid, newTax: newTaxValue });
        }

        delete originalTaxValuesRef.current[rowUuid];
      }
    },
    [tax.rows]
  );

  const confirmTaxAllocation = useCallback(() => {
    if (taxConfirm.maThueUuid && taxConfirm.newTax !== undefined) {
      allocateDetailTax(taxConfirm.maThueUuid, Number(taxConfirm.newTax));
      const totalTax = (tax.rows || []).reduce((s: number, r: MuaHangRecord) => s + Number(r.t_thue_nt || 0), 0);
      allocateAllDetailTax(totalTax);
    }
    setTaxConfirm({ open: false });
  }, [allocateAllDetailTax, allocateDetailTax, tax.rows, taxConfirm.maThueUuid, taxConfirm.newTax]);

  return {
    formMode,
    initialData,
    initialValues: initialData || initialFormValues,
    activeTab,
    title: getFormTitle(formMode, activeTab),
    actionButtons: getFormActionButtons(formMode, activeTab, {
      onAdd,
      onEdit,
      onDelete,
      onCopy,
      handleClose
    }),
    schema,
    constraints,
    formState,
    onClose: handleClose,
    submit,
    totals,
    detail: {
      ...detail,
      repeatRows: repeatDetailRows
    },
    tax: {
      ...tax,
      cellValueChange: taxCellValueChange,
      cellCommit: taxCellCommit,
      importFromDetail: importTaxFromDetail
    },
    tscc,
    taxConfirm: {
      open: taxConfirm.open,
      close: () => setTaxConfirm({ open: false }),
      confirm: confirmTaxAllocation
    }
  };
};

export type FormDialogModel = ReturnType<typeof useFormDialog>;
